import { Flex } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { setPerformanceTab } from '../../../store/strategy-store/strategy-store.actions';
import { baseWhite, inkLight } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { PerformanceTab } from '../initiative-performance/initiative-performance.type';
import { StrategyEntityType } from '../strategy.type';

type Props = {
  entityType: StrategyEntityType;
};

export const StrategyPerformanceTabs = ({ entityType }: Props) => {
  const i18n = useTranslation();
  const activeTab = useStrategyStore((state) => state.performanceTab);

  // Define which tabs to show for each entity type
  const getTabsForEntity = () => {
    const commonTabs = [
      {
        id: PerformanceTab.Throughput,
        label: i18n.t('strategy.initiative_performance.throughput'),
        icon: 'calendar_month',
      },
      { id: PerformanceTab.Focus, label: i18n.t('strategy.initiative_performance.focus'), icon: 'center_focus_strong' },
      { id: PerformanceTab.Cost, label: i18n.t('strategy.initiative_performance.cost'), icon: 'payments' },
      {
        id: PerformanceTab.RelatedTasks,
        label: i18n.t('common.related_tasks', { work_units: i18n.t('common.work_units') }),
        icon: 'task_alt',
      },
    ];

    // Initiatives get all tabs including FlowByPhase and Targets
    if (entityType === StrategyEntityType.Initiative) {
      return [
        commonTabs[0], // Throughput
        commonTabs[1], // Focus
        {
          id: PerformanceTab.FlowByPhase,
          label: i18n.t('strategy.initiative_performance.flow_by_phase'),
          icon: 'swap_calls',
        },
        { id: PerformanceTab.Targets, label: i18n.t('strategy.initiative_performance.targets'), icon: 'ads_click' },
        commonTabs[2], // Cost
        commonTabs[3], // Related Work Units
      ];
    } else if (entityType === StrategyEntityType.Outcome) {
      return [
        commonTabs[0], // Throughput
        commonTabs[1], // Focus
        { id: PerformanceTab.Milestones, label: i18n.t('strategy.initiative_performance.milestones'), icon: 'flag' },
        commonTabs[2], // Cost
        commonTabs[3], // Related Work Units
      ];
    } else if (entityType === StrategyEntityType.Epic) {
      return [
        commonTabs[0], // Throughput
        commonTabs[3], // Related Work Units
      ];
    } else if (entityType === StrategyEntityType.Goal) {
      return commonTabs;
    }
    // Goals and Outcomes only get common tabs
    return commonTabs;
  };

  const tabs = getTabsForEntity();

  return (
    <Flex gap={8} style={{ overflowX: 'auto' }}>
      {tabs.map((tab) => (
        <Button
          key={tab.id}
          radius="xl"
          size="xs"
          variant={activeTab === tab.id ? 'secondary' : 'outline'}
          onClick={() => setPerformanceTab(tab.id)}
          leftSection={<Icon name={tab.icon} size={14} color={activeTab === tab.id ? baseWhite : inkLight} pointer />}
          noAnimation
        >
          <Text size="small">{tab.label}</Text>
        </Button>
      ))}
    </Flex>
  );
};
